import React from "react";
import Center from "../Base/Center";
import Icon from "../Icon/Icon";
import Spinner from "../Spinner/Spinner";

type QwertyKeyboardProps = {
  value: string;
  setValue: (value: string) => void;
  onChange?: (value: string) => void;
  onEnter?: (value: string) => void;
  isPending?: boolean;
  withSpace?: boolean;
};

const ROWS = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
  ["a", "s", "d", "f", "g", "h", "j", "k", "l", ""],
  ["delete", "", "z", "x", "c", "v", "b", "n", "m", "enter"],
];

export const QwertyKeyboard: React.FC<QwertyKeyboardProps> = ({
  onChange,
  onEnter,
  setValue,
  value,
  isPending,
  withSpace = false,
}) => {
  const addChar = (char: string) => {
    const next = value + char;
    setValue(next);
    onChange?.(next);
  };

  const removeLast = () => {
    const next = value.slice(0, -1);
    setValue(next);
    onChange?.(next);
  };

  const handleKey = (key: string) => {
    if (key === "delete") return removeLast();
    if (key === "enter") return onEnter?.(value);
    if (!key) return;
    addChar(key);
  };

  const renderKeyContent = (key: string) => {
    if (key === "delete") {
      return <Icon className="h-[2.2rem] w-[2.2rem] fill-white" variants="deleteLeft" />;
    }

    if (key === "enter") {
      if (isPending) {
        return (
          <Center>
            <Spinner color="white" size="0.3rem" />
          </Center>
        );
      }
      return <Icon className="h-[2.4rem] w-[2.4rem] fill-white" variants="arrowLeft" />;
    }

    return key.toUpperCase();
  };

  return (
    <div className="flex w-full flex-col gap-[0.6rem]">
      {ROWS.map((row, rowIndex) => (
        <div key={rowIndex} className="grid grid-cols-[repeat(10,1fr)] gap-[0.6rem]">
          {row.map((key, keyIndex) => (
            <button
              key={`${key}-${keyIndex}`}
              className="flex aspect-square w-full items-center justify-center rounded-xl border-none text-[clamp(2.75rem,2.5vw,1.4rem)] font-black text-white active:scale-95"
              style={{
                background:
                  key === "enter"
                    ? "var(--success)"
                    : key === "delete"
                    ? "var(--error)"
                    : "var(--primary)",
              }}
              onClick={() =>
                isPending ? undefined : key ? handleKey(key) : undefined
              }
            >
              {renderKeyContent(key)}
            </button>
          ))}
        </div>
      ))}
      {withSpace && (
        <button
          type="button"
          className="flex h-[clamp(4rem,6vw,5.5rem)] w-full items-center justify-center rounded-xl border-none bg-primary text-[clamp(2rem,2vw,1.4rem)] font-black text-white active:scale-95"
          onClick={() => (isPending ? undefined : addChar(" "))}
        >
          ⎵
        </button>
      )}
    </div>
  );
};
